// liveScoringRates.js — DD-STAT-CONSOLIDATION
// SSoT for the live-scoring locked rule, shared by desktop LiveScoring and mobile
// MobileLiveScoring (Bible §14 separate trees import the SAME logic here).
//
// GM-LOCKED RULE: on the live-scoring surface, PLAYER rows show DAY counting stats but
// SEASON rate stats (a 3-for-4 must never read .750); the TEAM TOTALS row is DAY
// throughout. Rate columns are recomputed from emitted season COMPONENTS — never a
// pre-averaged/blended scalar. Missing season data → honest '-', never a fabricated 0.

import { injectScoringCategories } from './statRegistry';
import {
  battingAvg as computeAvg,
  obp as computeObp,
  slg as computeSlg,
  ops as computeOps,
  era as computeEra,
  whip as computeWhip,
} from './statTotals';

// The live-scoring payload emits flat SHORT keys (ab, h, 2b, era, …), not canonical
// fields. Map a registry field → the payload key.
export const LIVE_KEY = {
  // hitting
  at_bats: 'ab', runs: 'r', hits: 'h', doubles: '2b', triples: '3b', home_runs: 'hr',
  rbi: 'rbi', walks: 'bb', stolen_bases: 'sb', strikeouts: 'so',
  batting_avg: 'avg', obp: 'obp', slg: 'slg', ops: 'ops',
  // pitching
  innings_pitched: 'ip', wins: 'w', losses: 'l', saves: 'sv', holds: 'hd', blown_saves: 'bs',
  quality_starts: 'qs', hits_allowed: 'h', runs_allowed: 'r', earned_runs: 'er',
  home_runs_allowed: 'hr', walks_allowed: 'bb', strikeouts_pitched: 'so', games_played: 'g',
  era: 'era', whip: 'whip',
};
export const liveKeyFor = (field) => {
  const simple = field && field.includes('.') ? field.split('.').pop() : field;
  return LIVE_KEY[simple] || simple;
};

// Format a rate value. AVG/OBP/SLG/OPS → 3 dp, leading-0 stripped; ERA → 2 dp;
// WHIP → 3 dp (GM-locked). null/'-'/undefined → '-'.
export const formatAvg = (val) => {
  if (val === '-' || val === null || val === undefined) return '-';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return val;
  return num.toFixed(3).replace(/^0/, '');
};
export const formatRate = (abbrev, val) => {
  if (val === '-' || val === null || val === undefined) return '-';
  if (abbrev === 'ERA') return typeof val === 'number' ? val.toFixed(2) : val;
  if (abbrev === 'WHIP') return typeof val === 'number' ? val.toFixed(3) : val;
  return formatAvg(val);
};

// Pure SEASON rate for a player row, recomputed from emitted season COMPONENTS
// (never the legacy blended scalar). Returns null → honest '-' when the denominator
// is absent (no season components emitted, or 0 AB / 0 IP).
export function seasonRate(abbrev, season) {
  if (!season) return null;
  switch (abbrev) {
    case 'AVG':
      return season.at_bats > 0 ? computeAvg(season.hits, season.at_bats) : null;
    case 'OBP': {
      const denom = (season.at_bats || 0) + (season.walks || 0) + (season.hit_by_pitch || 0) + (season.sacrifice_flies || 0);
      return denom > 0 ? computeObp(season.hits, season.walks, season.hit_by_pitch, season.at_bats, season.sacrifice_flies) : null;
    }
    case 'SLG':
      return season.at_bats > 0 ? computeSlg(season.hits, season.doubles, season.triples, season.home_runs, season.at_bats) : null;
    case 'OPS': {
      const o = seasonRate('OBP', season);
      const s = seasonRate('SLG', season);
      return (o == null || s == null) ? null : computeOps(o, s);
    }
    case 'ERA':
      return season.innings_pitched > 0 ? computeEra(season.earned_runs, season.innings_pitched) : null;
    case 'WHIP':
      return season.innings_pitched > 0 ? computeWhip(season.hits_allowed, season.walks_allowed, season.innings_pitched) : null;
    default:
      return null;
  }
}

// Build the live-scoring column plan through the SSoT registry — context stats +
// league scoring categories, deduped and forced into CANONICAL order. One column
// source → the static/dynamic duplicate class cannot occur. Returns
// [{abbrev, label, isRate, liveKey, isScoring}].
export const buildLiveScoringColumns = (leagueCats, statType, contextKeys) => {
  const wanted = [];
  const seen = new Set();
  for (const k of [...(contextKeys || []), ...(leagueCats || [])]) {
    if (!seen.has(k)) { seen.add(k); wanted.push(k); }
  }
  const scoringSet = new Set(leagueCats || []);
  return injectScoringCategories(wanted, statType).map((cfg) => {
    const abbrev = cfg.abbrev || cfg.label;
    return {
      abbrev,
      label: cfg.label,
      isRate: !!cfg.isRate,
      liveKey: liveKeyFor(cfg.field),
      isScoring: scoringSet.has(abbrev),
    };
  });
};

export const HITTING_CONTEXT = ['AB', 'H', 'BB'];
export const PITCHING_CONTEXT = ['IP', 'H', 'BB', 'R', 'ER'];
